import { MoveRowsModule } from 'tabulator-tables';

// Feel-tuning knob: how far past a row's leading edge the drag has to travel
// before that row becomes the drop target. Raise it for a stickier drag, lower
// it for a twitchier one. Applied to both directions so they stay matched, and
// in pixels rather than a fraction of the row so a tall wrapped row and a
// single-line row trigger at the same perceived moment.
const DROP_DEADZONE_PX = 12;

/**
 * Replaces Tabulator MoveRows' drop-target decision. Two separate changes, one
 * a maths correction and one a deliberate behaviour change.
 *
 * Upstream (vendored 6.4.0):
 *   node_modules/tabulator-tables/src/js/modules/MoveRows/MoveRows.js
 *     - line 88  — `initializeGroupHeader`'s `config.mousemove`
 *     - line 122 — `initializeRow`'s `config.mousemove`
 *
 * ---------------------------------------------------------------------------
 * 1. Maths correction (both methods)
 * ---------------------------------------------------------------------------
 * Upstream compares the pointer's offset within the hovered row against half the
 * row's height, as:
 *
 *   (e.pageY - Helpers.elOffset(rowEl).top) + rowManager.element.scrollTop
 *
 * `Helpers.elOffset` (Helpers.js:7-14) already returns a *document-space* top
 * (`box.top + window.pageYOffset`), and `e.pageY` is document-space too — so the
 * subtraction is already the correct in-row offset and the added `scrollTop`
 * double-counts the holder's internal scroll, biasing decisions toward "insert
 * after". Measuring in viewport space on both sides
 * (`e.clientY - rowEl.getBoundingClientRect().top`) is immune to every scroll
 * container.
 *
 * Measured in this app the two expressions are numerically identical
 * (`stock === corrected`, `scrollTop === 0`): no Amba table sets a Tabulator
 * `height`, so the tableholder never scrolls (`clientHeight === scrollHeight`
 * even at 40 rows). The correction is therefore a no-op today and becomes
 * load-bearing the moment a `movable_rows:` table gains a scrolling body. This
 * is why it alone did not change the drag feel.
 *
 * ---------------------------------------------------------------------------
 * 2. Behaviour change: direction-aware whole-row drop zones (`initializeRow`)
 * ---------------------------------------------------------------------------
 * The midpoint test is gone for data rows. The drop side is now decided by where
 * the hovered row sits relative to the placeholder — the dragged row's current
 * slot — rather than by where in the row the pointer happens to be:
 *
 *   hovered row is BEFORE the placeholder (dragging up)   → insert BEFORE it
 *   hovered row is AFTER  the placeholder (dragging down) → insert AFTER  it
 *
 * So any hover anywhere on a row targets the near side of it, and there is no
 * half-row the user has to find. Two consequences worth knowing:
 *
 *   - `if (self.toRow === row) return;` replaces upstream's
 *     `self.toRow !== row || !self.toRowAfter` guard. It has to be the stronger
 *     guard, because our own insertion *flips* the hovered row's position
 *     relative to the placeholder; re-deciding on the next mousemove over the
 *     same row would oscillate the placeholder back and forth every frame. The
 *     direction for a row is settled the first time it is hovered and is
 *     re-decided when the pointer reaches a different row.
 *   - upstream's `if (rowEl.previousSibling)` gate on the before-branch is
 *     dropped: it blocked inserting above the first row, which direction-aware
 *     targeting needs in order to drag something to the top.
 *
 * `initializeGroupHeader` deliberately keeps the midpoint test (with the
 * corrected maths). Direction-awareness is not meaningful on a group header:
 * `after: false` on a group is redirected by GroupRows to the previous display
 * row (`GroupRows.js` `rowMoving`), so "before this header" means "end of the
 * previous group" — a real target, and not the one a direction rule would pick.
 *
 * ---------------------------------------------------------------------------
 * 3. Behaviour change: downward drops fire from the clone's leading edge
 * ---------------------------------------------------------------------------
 * Up and down were not equally sensitive, because the drop decision comes from
 * the *pointer* while the floating clone is pinned at the grab offset:
 * `setStartPosition` (MoveRows.js:353) stores `startY` as the pointer's offset
 * inside the row, and `moveHoverTable` (MoveRows.js:~400) positions the clone at
 * `yPos - startY`. The clone therefore extends `rowHeight - startY` *below* the
 * pointer:
 *
 *   dragging up   — the clone's top edge leads the pointer, so by the time the
 *                   pointer enters the row above, the clone already covers it.
 *                   Feels instant.
 *   dragging down — the clone's bottom edge visually overlaps the next row long
 *                   before the pointer reaches it. Feels dead.
 *
 * So the downward decision is moved off the pointer and onto the clone's leading
 * (bottom) edge, in `moveHoverTable` — which already runs on every mousemove of
 * a live drag, so no extra listener is needed. `checkDownwardDrop` walks the
 * placeholder's following siblings and targets the last data row whose top edge
 * the clone's bottom has passed. Upward behaviour is untouched and stays
 * pointer-driven in the per-row `mousemove`.
 *
 * The two paths cannot fight: both go through `moveRow` and both respect the
 * `toRow === row` guard, and the downward check only ever considers rows *after*
 * the placeholder — which the pointer-driven path reaches later, and then
 * returns early on. Group headers are skipped while walking, so their own
 * handler still owns cross-group drops and GroupRows' redirection is unaffected.
 * A clone bottom below the last row simply targets the last row.
 *
 * ---------------------------------------------------------------------------
 * 4. Dead-zone (`DROP_DEADZONE_PX`)
 * ---------------------------------------------------------------------------
 * Pure edge crossing was too twitchy, so a row only becomes the target once the
 * drag is `DROP_DEADZONE_PX` *past* its leading edge. Applied to both directions
 * so neither is more eager than the other:
 *
 *   down — clone bottom must reach `row.top + DEADZONE`
 *   up   — pointer must reach `row.bottom - DEADZONE` (coming up, the pointer
 *          crosses the row's *bottom* edge first, so the zone is measured there)
 *
 * The pointer-driven downward branch in the row `mousemove` gets the mirrored
 * guard (`row.top + DEADZONE`) so it agrees with the clone-driven path on the
 * rare occasions it wins the race.
 *
 * Height independence (this table has variable-height rows — the Guide column
 * wraps). The dead-zone is in pixels, and every test is an edge crossing between
 * two independent elements rather than a fraction of a row, so a short row
 * dragged over a tall one and a tall row dragged over a short one trigger at the
 * same perceived moment. This assumes rows are comfortably taller than twice the
 * dead-zone; the shortest single-line row here is ~35px (8px padding each side
 * plus a line), against the `DROP_DEADZONE_PX` zone. The only
 * height-proportional test left in the module is the group-header midpoint at
 * `initializeGroupHeader`, deliberately kept as upstream.
 *
 * Everything else in both methods is copied byte-identically from the vendored
 * source, so a Tabulator upgrade can re-diff them cheaply. The touch path
 * (`config.touchmove` / `bindTouchEvents`) is untouched, so touch drags keep
 * upstream's neighbour-stepping behaviour — `moveHoverTable` skips the downward
 * check when `touchMove` is set.
 */

MoveRowsModule.prototype.initializeGroupHeader = function (group) {
  var self = this,
    config = {};

  //inter table drag drop
  config.mouseup = function (e) {
    self.tableRowDrop(e, group);
  }.bind(self);

  //same table drag drop
  config.mousemove = function (e) {
    var rowEl;

    if (
      e.clientY - group.element.getBoundingClientRect().top >
      group.getHeight() / 2
    ) {
      if (self.toRow !== group || !self.toRowAfter) {
        rowEl = group.getElement();
        rowEl.parentNode.insertBefore(
          self.placeholderElement,
          rowEl.nextSibling
        );
        self.moveRow(group, true);
      }
    } else {
      if (self.toRow !== group || self.toRowAfter) {
        rowEl = group.getElement();
        if (rowEl.previousSibling) {
          rowEl.parentNode.insertBefore(self.placeholderElement, rowEl);
          self.moveRow(group, false);
        }
      }
    }
  }.bind(self);

  group.modules.moveRow = config;
};

MoveRowsModule.prototype.initializeRow = function (row) {
  var self = this,
    config = {},
    rowEl;

  //inter table drag drop
  config.mouseup = function (e) {
    self.tableRowDrop(e, row);
  }.bind(self);

  //same table drag drop — direction-aware whole-row targeting (see header)
  config.mousemove = function (e) {
    var rowEl = row.getElement();

    // Already targeting this row: the side is settled. Re-deciding here would
    // oscillate, because the insertion below moves the placeholder past rowEl.
    if (self.toRow === row) {
      return;
    }

    // Is the hovered row after the placeholder in document order? Then the drag
    // is heading down and the row's far side is the near one.
    var after = !!(
      self.placeholderElement.compareDocumentPosition(rowEl) &
      Node.DOCUMENT_POSITION_FOLLOWING
    );

    // Dead-zone, measured from whichever edge the pointer crossed to get here:
    // coming down it entered over the row's top, coming up over its bottom.
    var rect = rowEl.getBoundingClientRect();

    if (after) {
      if (e.clientY < rect.top + DROP_DEADZONE_PX) {
        return;
      }
      rowEl.parentNode.insertBefore(self.placeholderElement, rowEl.nextSibling);
      self.moveRow(row, true);
    } else {
      if (e.clientY > rect.bottom - DROP_DEADZONE_PX) {
        return;
      }
      rowEl.parentNode.insertBefore(self.placeholderElement, rowEl);
      self.moveRow(row, false);
    }
  }.bind(self);

  if (!this.hasHandle) {
    rowEl = row.getElement();

    rowEl.addEventListener('mousedown', function (e) {
      if (e.which === 1) {
        self.checkTimeout = setTimeout(function () {
          self.startMove(e, row);
        }, self.checkPeriod);
      }
    });

    rowEl.addEventListener('mouseup', function (e) {
      if (e.which === 1) {
        if (self.checkTimeout) {
          clearTimeout(self.checkTimeout);
        }
      }
    });

    this.bindTouchEvents(row, row.getElement());
  }

  row.modules.moveRow = config;
};

// Upstream MoveRows.js:399-405, plus the trailing downward-drop check.
MoveRowsModule.prototype.moveHoverTable = function (e) {
  var rowHolder = this.table.rowManager.getElement(),
    scrollTop = rowHolder.scrollTop,
    yPos =
      (this.touchMove ? e.touches[0].pageY : e.pageY) -
      rowHolder.getBoundingClientRect().top +
      scrollTop;

  this.hoverElement.style.top =
    Math.min(
      yPos - this.startY,
      this.table.rowManager.element.scrollHeight -
        this.hoverElement.offsetHeight
    ) + 'px';

  // Mouse only: the touch path steps through neighbours on its own.
  if (!this.touchMove) {
    this.checkDownwardDrop();
  }
};

// Amba addition: fire the downward drop from the clone's leading (bottom) edge
// instead of the pointer, so dragging down is as responsive as dragging up.
MoveRowsModule.prototype.checkDownwardDrop = function () {
  var placeholder = this.placeholderElement;

  if (!placeholder || !placeholder.parentNode || !this.hoverElement) {
    return;
  }

  var bottom = this.hoverElement.getBoundingClientRect().bottom;
  var candidate = null;
  var el = placeholder.nextElementSibling;

  // The last data row below the placeholder whose top the clone has passed.
  // Group headers are stepped over so a downward drag can still cross into the
  // next category; their own handler keeps owning drops onto the header itself.
  while (el) {
    if (
      el !== this.hoverElement &&
      el.classList.contains('tabulator-row') &&
      !el.classList.contains('tabulator-group')
    ) {
      if (bottom <= el.getBoundingClientRect().top + DROP_DEADZONE_PX) {
        break;
      }
      candidate = el;
    }
    el = el.nextElementSibling;
  }

  if (!candidate) {
    return;
  }

  var row = this.table.rowManager
    .getRows()
    .find((candidateRow) => candidateRow.getElement() === candidate);

  // Same guard as the per-row mousemove: a row already targeted is settled, so
  // the two paths agree instead of fighting.
  if (!row || this.toRow === row) {
    return;
  }

  candidate.parentNode.insertBefore(placeholder, candidate.nextSibling);
  this.moveRow(row, true);
};
